import { connect } from 'react-redux'
import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { navigationEvent } from 'layers/navigation/store/operations'
import ContentEntryDataGetter from 'layers/content/Hocs/ContentEntryDataGetter'
import withApiError from 'layers/errorHandling/apiError/component'
import {
  ERROR_TYPE_CONTENTFUL,
  SHOW_CONTENT_ON_401,
} from 'layers/errorHandling/apiError'
import NavigationHelper from 'layers/navigation/navigationHelper'
import { operations as userSessionOperations } from 'store/userSessionManager'
import { TOGGLE_VIEW } from 'store/userSessionManager/constants'
import { operations as loadingOperations } from 'store/loadingManager'
import {
  operations as licenseOperations,
  selectors as licenseSelectors,
} from 'store/licenseManager'
import { isProgramEnabledAtSite } from 'store/licenseManager/siteProgramAccess'
import { operations as reportsOperations } from 'store/reportsManager'

import AdminPage from './component'
import { LOADING_MESSAGE } from './constants'
import { FLAG_CORE_LESSON_COMPLETION_UPDATES } from 'components/AdminDashboardBanner/constants'
import { isFlagOn } from 'utils/featureFlags'

const canUseRecentLeaderDashboardUrl = (
  recentDashboardUrl,
  claims,
  programPath,
  hasAccessToSelaProgram,
  hasStandaloneDwbAccess,
) => {
  if (!recentDashboardUrl) return false
  const recent = recentDashboardUrl
    .match(/program\/([^/]+)/)?.[1]
    ?.toLowerCase()
  const access = {
    k8: claims?.hasElemAdminClaim || claims?.hasMsAdminClaim,
    hs: claims?.hasHighschoolAdminClaim,
    cpu: claims?.hasCpuAdminClaim,
    bpu: claims?.hasBpuAdminClaim,
    sela: hasAccessToSelaProgram,
    dwb: hasStandaloneDwbAccess,
  }
  if (!recent || !access[recent]) return false
  const current = programPath?.toLowerCase()
  if (current && access[current]) return false
  return true
}

export const AdminDashboardContainer = ({
  activeAdminK8Sites,
  activeAdminStandaloneDwbSites,
  adminK8SitesWithDwb,
  activeAdminSites,
  adminHSSites,
  adminHsSitesWithDwb,
  claims,
  deleteReportsData,
  displayTitle,
  fetchUserSites,
  hasAccessToSelaProgram,
  isFetching: isFetchingContentful,
  isFetchingUserContext,
  isFetchingUserSites,
  hsToggleView,
  k8ToggleView,
  location,
  match,
  navigationEvent,
  onboardingSnapshot,
  programs,
  programTitles,
  recentDashboardUrl,
  selectedProgram,
  setAppIsLoadingState,
  setAppNotLoadingState,
  setLeaderDashboardRecentUrl,
  setHSToggleView,
  setK8ToggleView,
  updateProgramsForUser,
  updateProgramTitles,
  updateSelectedProgram,
}) => {
  const [program, setProgram] = useState()
  const { params } = match
  const { programName: programPath, viewOption } = params || {}
  const hasStandaloneDwbAccess =
    isFlagOn([FLAG_CORE_LESSON_COMPLETION_UPDATES]) &&
    activeAdminStandaloneDwbSites?.length > 0
  // Helper function to get default view for a program based on site count
  const getDefaultViewForProgram = programSlug => {
    if (programSlug === 'k8') {
      return 'setup'
    } else if (programSlug === 'hs') {
      return 'monitor'
    }
    return 'monitor'
  }

  const setToggleViewState = viewOption => {
    const toggleViewMap = {
      interventions: TOGGLE_VIEW.INTERVENTIONS,
      core: TOGGLE_VIEW.CORE_UNITS,
      launch: TOGGLE_VIEW.LAUNCH,
      specialized_units: TOGGLE_VIEW.SPECIALIZED_UNITS,
      monitor: TOGGLE_VIEW.MONITOR,
      setup: TOGGLE_VIEW.SETUP,
    }

    const toggleView = toggleViewMap[viewOption]
    if (!toggleView) return
    if (programPath === 'k8' && viewOption === 'launch') {
      navigationEvent('/admin/program/k8/core', NavigationHelper.types.REPLACE)
      setK8ToggleView(TOGGLE_VIEW.CORE_UNITS)
      return
    }

    if (programPath === 'k8') {
      if (viewOption === 'monitor') {
        setK8ToggleView(
          isFlagOn([FLAG_CORE_LESSON_COMPLETION_UPDATES])
            ? TOGGLE_VIEW.CORE_UNITS
            : TOGGLE_VIEW.MONITOR,
        )
      } else {
        setK8ToggleView(toggleView)
      }
    } else if (programPath === 'hs') {
      setHSToggleView(toggleView)
    }
  }

  const setDefaultToggleViews = () => {
    const k8Sites = activeAdminK8Sites?.length || 0
    const hsSites = adminHSSites?.length || 0
    if (k8Sites > 0) {
      setK8ToggleView(TOGGLE_VIEW.CORE_UNITS)
    }

    if (hsSites > 0) {
      setHSToggleView(TOGGLE_VIEW.MONITOR)
    }
  }

  // Helper function to handle navigation
  const handleNavigation = programTitlesParsed => {
    if (isFetchingUserSites) {
      return
    }

    // Always set program state if we have programPath (needed for component rendering)
    if (programPath) {
      const programName = programPath.toUpperCase()
      setProgram(programName)
      updateSelectedProgram({ key: programName, name: programName })
    }

    // Safeguard: Check if URL is already in correct format (skip navigation but keep state updates)
    const expectedUrlPattern = /^\/admin\/program\/[a-z0-9]+\/[a-z_]+$/
    if (
      expectedUrlPattern.test(location.pathname) &&
      viewOption &&
      programPath
    ) {
      return
    }

    if (programTitlesParsed.length > 0 && match.path === '/admin') {
      // Navigate from base /admin path
      if (
        canUseRecentLeaderDashboardUrl(
          recentDashboardUrl,
          claims,
          programPath,
          hasAccessToSelaProgram,
          hasStandaloneDwbAccess,
        )
      ) {
        const targetUrl = `/admin/${recentDashboardUrl}${location.search}`
        navigationEvent(targetUrl, NavigationHelper.types.REPLACE)
      } else {
        const defaultProgram = programTitlesParsed[0]?.toLowerCase()
        const defaultView = getDefaultViewForProgram(defaultProgram)
        const path = ['k8', 'hs'].includes(defaultProgram)
          ? `${defaultProgram}/${defaultView}`
          : defaultProgram
        const targetUrl = `/admin/program/${path}${location.search}`
        navigationEvent(targetUrl, NavigationHelper.types.REPLACE)
      }
    } else if (programTitlesParsed.length > 0) {
      // Navigate within program paths
      const programSlug = programPath.toLowerCase()
      const defaultView = getDefaultViewForProgram(programSlug)

      if (['k8', 'hs'].includes(programSlug)) {
        // Only navigate if we don't have a viewOption or if URL is malformed
        if (
          !viewOption ||
          location.pathname.includes(`/${programSlug}/${programSlug}/`)
        ) {
          const targetUrl = `${programSlug}/${viewOption || defaultView}${
            location.search
          }`
          navigationEvent(targetUrl, NavigationHelper.types.REPLACE)
        }
      } else if (!viewOption) {
        const targetUrl = `${programSlug}${location.search}`
        navigationEvent(targetUrl, NavigationHelper.types.REPLACE)
      }
    }
  }

  // Helper function to build aggregate programs
  const buildAggregatePrograms = () => {
    const aggregatePrograms = {
      BPU: new Set(),
      CPU: new Set(),
      HS: new Set(),
      K8: new Set(),
      SELA: new Set(),
    }

    if (isFlagOn([FLAG_CORE_LESSON_COMPLETION_UPDATES])) {
      aggregatePrograms.DWB = new Set()
      activeAdminStandaloneDwbSites?.forEach(site => {
        aggregatePrograms.DWB.add(site)
      })
    }

    activeAdminSites.forEach(site => {
      if (
        (claims?.hasElemAdminClaim || claims?.hasMsAdminClaim) &&
        isProgramEnabledAtSite(site, 'K8')
      ) {
        aggregatePrograms.K8.add(site)
      }
      if (
        claims?.hasHighschoolAdminClaim &&
        isProgramEnabledAtSite(site, 'HS')
      ) {
        aggregatePrograms.HS.add(site)
      }
      if (claims?.hasBpuAdminClaim && isProgramEnabledAtSite(site, 'BPU')) {
        aggregatePrograms.BPU.add(site)
      }
      if (claims?.hasCpuAdminClaim && isProgramEnabledAtSite(site, 'CPU')) {
        aggregatePrograms.CPU.add(site)
      }
      if (hasAccessToSelaProgram && isProgramEnabledAtSite(site, 'SELA')) {
        aggregatePrograms.SELA.add(site)
      }
    })

    // Convert sets to arrays and filter empty programs
    const programTitlesParsed = Object.keys(aggregatePrograms).filter(
      program => aggregatePrograms[program].size > 0,
    )

    Object.keys(aggregatePrograms).forEach(program => {
      aggregatePrograms[program] = [...aggregatePrograms[program]]
    })

    return { aggregatePrograms, programTitlesParsed }
  }

  useEffect(() => {
    const cleanup = async () => {
      await deleteReportsData()
    }
    return cleanup
  }, [])

  useEffect(() => {
    if (!activeAdminSites) {
      fetchUserSites()
    } else if (activeAdminSites?.length > 0) {
      const {
        aggregatePrograms,
        programTitlesParsed,
      } = buildAggregatePrograms()

      updateProgramTitles(programTitlesParsed)
      updateProgramsForUser(aggregatePrograms)

      // Initial load - no selectedProgram, need to navigate
      if (!selectedProgram) {
        handleNavigation(programTitlesParsed)

        // Set toggle view state based on current URL, unless recentDashboardUrl is present
        if (!recentDashboardUrl) {
          if (viewOption) {
            setToggleViewState(viewOption)
          } else {
            setDefaultToggleViews()
          }
        }
      }
    }

    if (!viewOption) {
      if (
        canUseRecentLeaderDashboardUrl(
          recentDashboardUrl,
          claims,
          programPath,
          hasAccessToSelaProgram,
          hasStandaloneDwbAccess,
        )
      ) {
        const targetUrl = `/admin/${recentDashboardUrl}${location.search}`
        navigationEvent(targetUrl, NavigationHelper.types.REPLACE)
        setToggleViewState(recentDashboardUrl)
      } else {
        setDefaultToggleViews()
      }
    } else {
      setToggleViewState(viewOption)
    }

    // Handle loading state
    if (isFetchingContentful || isFetchingUserContext || isFetchingUserSites) {
      setAppIsLoadingState(LOADING_MESSAGE)
    } else {
      setAppNotLoadingState()
    }
  }, [
    activeAdminSites,
    activeAdminStandaloneDwbSites,
    displayTitle,
    isFetchingUserContext,
    isFetchingUserSites,
    isFetchingContentful,
    match,
    selectedProgram,
    viewOption,
    recentDashboardUrl,
  ])

  const multiSiteAdmin = activeAdminK8Sites?.length > 1

  return (
    <AdminPage
      activeAdminK8Sites={activeAdminK8Sites}
      activeAdminStandaloneDwbSites={activeAdminStandaloneDwbSites}
      adminHSSites={adminHSSites}
      adminHsSitesWithDwb={adminHsSitesWithDwb}
      adminK8SitesWithDwb={adminK8SitesWithDwb}
      displayTitle={displayTitle}
      hsToggleView={hsToggleView}
      k8ToggleView={k8ToggleView}
      match={match}
      multiSiteAdmin={multiSiteAdmin}
      navigationEvent={navigationEvent}
      onboardingSnapshot={onboardingSnapshot}
      programTabName={program}
      programTitles={programTitles}
      programs={programs}
      selectedProgram={selectedProgram}
      setLeaderDashboardRecentUrl={setLeaderDashboardRecentUrl}
      updateSelectedProgram={updateSelectedProgram}
    />
  )
}

const mapStateToProps = state => {
  const {
    licenseManager,
    reportsManager,
    userAccessManager,
    userContextManager,
    userProfileManager: {
      profile: { roles },
    },
    userSessionManager: {
      adminDashboard: {
        hs: { toggleView: hsToggleView },
        k8: { toggleView: k8ToggleView },
        recentLeaderDashboardUrl: recentDashboardUrl,
      },
    },
  } = state
  const { isFetching: isFetchingUserContext } = userContextManager || {}
  const { isFetchingUserSites, isFetchingUserLicenses } = licenseManager || {}
  const { selectedProgram, programTitles, programs } = reportsManager || {}
  const { claims } = userAccessManager || {}
  const activeAdminSites = licenseSelectors.selectActiveSites(state)
  // TODO: refactor - below selectors are iterating activeAdminSites. Do it here in one iteration.
  const activeAdminK8Sites = licenseSelectors.selectActiveAdminK8Sites(state)
  const adminK8SitesWithDwb = licenseSelectors.selectActiveAdminK8SitesWithDwb(
    state,
  )
  const adminHSSites = licenseSelectors.selectActiveAdminHsSites(state)
  const adminHsSitesWithDwb = licenseSelectors.selectActiveAdminHsSitesWithDwb(
    state,
  )
  const activeAdminStandaloneDwbSites = licenseSelectors.selectActiveAdminStandaloneDwbSites(
    state,
  )

  return {
    activeAdminK8Sites,
    activeAdminStandaloneDwbSites,
    adminK8SitesWithDwb,
    activeAdminSites,
    adminHSSites,
    adminHsSitesWithDwb,
    claims,
    hasAccessToSelaProgram: claims?.hasSelaAdminClaim,
    isFetchingUserContext,
    isFetchingUserSites,
    isFetchingUserLicenses,
    hsToggleView,
    k8ToggleView,
    programs,
    programTitles,
    recentDashboardUrl,
    roles,
    selectedProgram,
    unassignedLicenses: licenseManager?.unassignedLicenses,
  }
}

const mapDispatchToProps = {
  deleteReportsData: reportsOperations.deleteReportsData,
  fetchUserSites: licenseOperations.fetchUserSites,
  fetchActiveProgramsForUser: reportsOperations.fetchActiveProgramsForUser,
  navigationEvent,
  setLeaderDashboardRecentUrl:
    userSessionOperations.setLeaderDashboardRecentUrl,
  setAppIsLoadingState: loadingOperations.setIsLoadingState,
  setAppNotLoadingState: loadingOperations.setNotLoadingState,
  setHSToggleView: userSessionOperations.adminSetHSToggleView,
  setK8ToggleView: userSessionOperations.adminSetK8ToggleView,
  updateProgramTitles: reportsOperations.updateProgramTitles,
  updateProgramsForUser: reportsOperations.updateProgramsForUser,
  updateSelectedProgram: reportsOperations.updateSelectedProgram,
}

const mapper = entry => {
  const { displayTitle, onboardingSnapshot } = entry || {}
  return {
    displayTitle,
    onboardingSnapshot,
  }
}

const options = {
  entryId: '5oqdbnI289AADFVmm2sJyx',
  include: 5,
  mapper,
  spread: true,
}

AdminDashboardContainer.propTypes = {
  activeAdminK8Sites: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  activeAdminSites: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  activeAdminStandaloneDwbSites: PropTypes.array,
  adminHSSites: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  adminHsSitesWithDwb: PropTypes.array,
  adminK8SitesWithDwb: PropTypes.array,
  claims: PropTypes.object,
  deleteReportsData: PropTypes.func,
  displayTitle: PropTypes.string,
  fetchUserSites: PropTypes.func,
  hasAccessToSelaProgram: PropTypes.bool,
  hsToggleView: PropTypes.string,
  isFetching: PropTypes.bool,
  isFetchingUserContext: PropTypes.bool,
  isFetchingUserSites: PropTypes.bool,
  k8ToggleView: PropTypes.string,
  location: PropTypes.object.isRequired,
  match: PropTypes.object,
  navigationEvent: PropTypes.func,
  onboardingSnapshot: PropTypes.array,
  programTitles: PropTypes.array,
  programs: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  recentDashboardUrl: PropTypes.string,
  selectedProgram: PropTypes.object,
  setAppIsLoadingState: PropTypes.func,
  setAppNotLoadingState: PropTypes.func,
  setHSToggleView: PropTypes.func,
  setK8ToggleView: PropTypes.func,
  setLeaderDashboardRecentUrl: PropTypes.func,
  updateProgramTitles: PropTypes.func,
  updateProgramsForUser: PropTypes.func,
  updateSelectedProgram: PropTypes.func,
}

export default ContentEntryDataGetter(
  withApiError(
    AdminDashboardContainer,
    [ERROR_TYPE_CONTENTFUL],
    [SHOW_CONTENT_ON_401],
  ),
  options,
  connect(mapStateToProps, mapDispatchToProps),
)
